import { memo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, Popconfirm } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { dateToString, getFullName } from '@/utils';
import { useDeleteGroupMemberMutation, useGetGroupMembersQuery } from '@/services/sibling-group-api';
import { EllipsisVSvg, SearchSvg, UserPlusSvg, UsersSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import Panel from '@/components/panel';
import AddModal from './add-modal';

const Siblings = ({ groupId }) => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const pageNumber = queryParams.get('page') || 1;
  const { data, isFetching, refetch } = useGetGroupMembersQuery({ groupId, pageNumber });

  const [deleteMember] = useDeleteGroupMemberMutation();

  const [isOpenAddModal, openAddModal] = useState(false);

  const columns = [
    {
      title: 'Child ID',
      dataIndex: 'childCode',
      render: (value, record) => <Link to={`/child/${record.childId}`}>{value}</Link>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value) => getFullName(value),
    },
    {
      title: 'DOB',
      dataIndex: 'birthInfo',
      render: (value) => {
        const d = dayjs(value.date);
        return (
          <>
            <div>{dateToString(d)}</div>({d.fromNow(true)})
          </>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value) => value.name,
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure to delete this?"
          icon={<QuestionCircleOutlined style={{ color: '#ff4d4f' }} />}
          okText="Yes"
          cancelText="No"
          onConfirm={async () => {
            try {
              await deleteMember({ groupId, memberId: record.childId }).unwrap();
              refetch();
            } catch (err) {}
          }}
        >
          <Button type="link" danger>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Panel
      icon={<UsersSvg />}
      title="Siblings"
      extra={
        <Dropdown
          menu={{
            items: [
              {
                key: 'add',
                icon: <UserPlusSvg />,
                label: <Link to={`/sibling-group/${groupId}/add-child`}>Add</Link>,
              },
              {
                key: 'search',
                icon: <SearchSvg />,
                label: 'Select Existing',
                onClick: () => openAddModal(true),
              },
            ].filter((item) => item),
          }}
        >
          <Button type="link" icon={<EllipsisVSvg fontSize="16" />} />
        </Dropdown>
      }
    >
      <Table
        columns={columns}
        dataSource={data?.data}
        rowKey="childId"
        loading={isFetching}
        pageInfo={data}
        size="small"
      />

      <AddModal groupId={groupId} open={isOpenAddModal} refetch={refetch} onClose={() => openAddModal(false)} />
    </Panel>
  );
};

export default memo(Siblings);
